import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, KeyRound, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { BRAND } from "@/data/content";

const AdminReset = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    if (!token) return toast.error("Missing reset token.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      navigate("/admin/login", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset link is invalid or expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1428] text-white flex items-center justify-center px-5 relative overflow-hidden">
      <div className="hero-orb absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full" />
      <Link to="/admin/login" className="absolute top-6 left-6 inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors" data-testid="reset-back">
        <ArrowLeft size={16} /> Back to sign in
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-8 md:p-10"
      >
        <div className="flex items-center gap-3 mb-8">
          <img src={BRAND.logo} alt={BRAND.name} className="h-12 w-12 rounded-lg object-contain" />
          <div className="font-display font-bold text-lg leading-none">TITAN<span className="text-[#1E6BFF]">.</span></div>
        </div>

        <h1 className="font-display font-bold text-3xl mb-2 flex items-center gap-2">
          <KeyRound size={22} className="text-[#1E6BFF]" /> New password
        </h1>
        <p className="text-gray-400 text-sm mb-8">Choose a strong password (min 8 characters).</p>

        <form onSubmit={submit} className="space-y-4">
          <Input
            type="password"
            data-testid="reset-password"
            placeholder="New password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="bg-[#050A14]/60 border-white/15 text-white placeholder:text-gray-500 focus-visible:ring-[#1E6BFF] h-12"
          />
          <Input
            type="password"
            data-testid="reset-confirm"
            placeholder="Confirm password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            className="bg-[#050A14]/60 border-white/15 text-white placeholder:text-gray-500 focus-visible:ring-[#1E6BFF] h-12"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="reset-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1E6BFF] hover:bg-[#3B82F6] disabled:opacity-60 text-white font-semibold h-12 transition-colors duration-200 glow-blue"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : "Update password"}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default AdminReset;
